// SCR-STAFF-201 تب زمان (US-CAT-010): مدت اصلی، بافر قبل و بعد (AC-CAT-010.1)، مدت به ازای واحد (AC-CAT-010.2) و استثنای
// ارائه‌دهنده (AC-CAT-010.3)؛ مدت ≥ ۵ و مضرب ۵ (BR-CAT-010).
import { zodResolver } from '@hookform/resolvers/zod';
import AddIcon from '@mui/icons-material/Add';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { unwrap, type DurationRuleDto, type ServiceView } from '@phoenix/api-client';
import { useTenantSession, useWriteAccess } from '@phoenix/auth';
import { useTranslation } from '@phoenix/i18n';
import {
  applyServerErrors,
  ErrorState,
  faDigits,
  LoadingState,
  SelectRHF,
  useSaveShortcut,
} from '@phoenix/ui';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useMemo, useState } from 'react';
import { useFieldArray, useForm, useWatch } from 'react-hook-form';
import { z } from 'zod';
import { WriteNotice } from '../settings/common';
import { DurationFields, durationSchema } from './CatalogDialogs';
import { catKeys, SavedNotice, useProviders } from './common';

const timeSchema = z
  .object({
    general: durationSchema,
    overrides: z.array(
      z.object({
        providerId: z.string().min(1, { error: 'common:validation.required' }),
        rule: durationSchema,
      }),
    ),
  })
  .superRefine((value, ctx) => {
    const seen = new Set<string>();
    value.overrides.forEach((item, index) => {
      if (seen.has(item.providerId)) {
        ctx.addIssue({
          code: 'custom',
          path: ['overrides', index, 'providerId'],
          message: 'errors:validation.duplicate',
        });
      }
      seen.add(item.providerId);
    });
  });
type TimeForm = z.input<typeof timeSchema>;

function toRule(rule: DurationRuleDto): TimeForm['general'] {
  return {
    durationMinutes: Number(rule.durationMinutes),
    bufferBeforeMinutes: Number(rule.bufferBeforeMinutes),
    bufferAfterMinutes: Number(rule.bufferAfterMinutes),
    perUnitMinutes:
      rule.perUnitMinutes === null || rule.perUnitMinutes === undefined ? null : Number(rule.perUnitMinutes),
  };
}

export function TimeTab({ service }: { service: ServiceView }) {
  const { t } = useTranslation('cat');
  const { tenantApi, session } = useTenantSession();
  const queryClient = useQueryClient();
  const write = useWriteAccess('cat.service.manage');
  const providers = useProviders();
  const [saved, setSaved] = useState(false);
  const [submitError, setSubmitError] = useState<unknown>();

  const durations = useQuery({
    queryKey: catKeys.durations(session?.tenantId, service.id),
    queryFn: () =>
      unwrap(
        tenantApi.GET('/api/v1/catalog/services/{id}/durations', { params: { path: { id: service.id } } }),
      ),
  });

  const values = useMemo<TimeForm | undefined>(
    () =>
      durations.data
        ? {
            general: toRule(durations.data.general),
            overrides: durations.data.overrides.map((item) => ({
              providerId: item.providerId,
              rule: toRule(item.rule),
            })),
          }
        : undefined,
    [durations.data],
  );
  const { control, handleSubmit, formState, setError } = useForm<TimeForm>({
    resolver: zodResolver(timeSchema),
    values,
  });
  const overrides = useFieldArray({ control, name: 'overrides' });
  const general = useWatch({ control, name: 'general' });

  const save = useMutation({
    mutationFn: (form: TimeForm) =>
      unwrap(
        tenantApi.PUT('/api/v1/catalog/services/{id}/durations', {
          params: { path: { id: service.id } },
          body: {
            general: { ...form.general, perUnitMinutes: form.general.perUnitMinutes ?? null },
            overrides: form.overrides.map((item) => ({
              providerId: item.providerId,
              rule: { ...item.rule, perUnitMinutes: item.rule.perUnitMinutes ?? null },
            })),
          },
        }),
      ),
    onMutate: () => {
      setSaved(false);
      setSubmitError(undefined);
    },
    onSuccess: () => {
      setSaved(true);
      void queryClient.invalidateQueries({ queryKey: catKeys.all(session?.tenantId) });
    },
    onError: (error) => {
      const applied = applyServerErrors(error, setError, {
        fields: ['general.durationMinutes'],
        codeFields: { 'PHX-CAT-0301': 'general.durationMinutes' },
      });
      setSubmitError(applied > 0 ? undefined : error);
    },
  });

  const submit = () => {
    if (write.canWrite) {
      void handleSubmit((form) => save.mutate(form))();
    }
  };
  useSaveShortcut(submit, write.canWrite && !save.isPending);

  if (durations.isPending) {
    return <LoadingState rows={3} />;
  }
  if (durations.error) {
    return <ErrorState error={durations.error} onRetry={() => void durations.refetch()} />;
  }

  // پیش‌نمایش (AC-CAT-010.2): مدت کل برای ۳ واحد = اصلی + به ازای واحد × ۲ + بافرها.
  const perUnit = general?.perUnitMinutes ?? null;
  const main3 = Number(general?.durationMinutes ?? 0) + (perUnit ? perUnit * 2 : 0);
  const total3 = main3 + Number(general?.bufferBeforeMinutes ?? 0) + Number(general?.bufferAfterMinutes ?? 0);

  return (
    <Paper variant="outlined" sx={{ p: 2 }}>
      <Box
        component="form"
        noValidate
        onSubmit={(event) => {
          event.preventDefault();
          submit();
        }}
      >
        <Stack spacing={2}>
          <WriteNotice write={write} />
          <SavedNotice show={saved && !formState.isDirty} />
          <Typography variant="subtitle1">{t('cat:duration.general')}</Typography>
          <Typography variant="body2" color="text.secondary">
            {t('cat:duration.explain')}
          </Typography>
          <DurationFields control={control} prefix="general" disabled={!write.canWrite} />
          {perUnit ? (
            <Typography variant="body2" data-testid="duration-preview">
              {t('cat:duration.preview', { main: faDigits(main3), total: faDigits(total3) })}
            </Typography>
          ) : null}
          <Stack direction="row" alignItems="center" justifyContent="space-between">
            <Typography variant="subtitle1">{t('cat:duration.overrides')}</Typography>
            <Button
              startIcon={<AddIcon />}
              disabled={!write.canWrite}
              onClick={() =>
                overrides.append({
                  providerId: '',
                  rule: { ...general, perUnitMinutes: general?.perUnitMinutes ?? null },
                })
              }
              data-testid="override-add"
            >
              {t('cat:duration.add_override')}
            </Button>
          </Stack>
          {overrides.fields.length === 0 && (
            <Typography variant="body2" color="text.secondary">
              {t('cat:duration.no_overrides')}
            </Typography>
          )}
          {overrides.fields.map((field, index) => (
            <Paper key={field.id} variant="outlined" sx={{ p: 1.5 }} data-testid={`override-${index}`}>
              <Stack spacing={1.5}>
                <Stack direction="row" spacing={1} alignItems="flex-start">
                  <SelectRHF
                    control={control}
                    name={`overrides.${index}.providerId`}
                    labelKey="cat:duration.provider"
                    required
                    fullWidth
                    disabled={!write.canWrite}
                    options={[
                      ...providers.options.map((option) => ({ value: option.id, label: option.name })),
                      ...(field.providerId &&
                      !providers.options.some((option) => option.id === field.providerId)
                        ? [{ value: field.providerId, label: providers.name(field.providerId) }]
                        : []),
                    ]}
                  />
                  <IconButton
                    aria-label={t('cat:duration.remove_override')}
                    disabled={!write.canWrite}
                    onClick={() => overrides.remove(index)}
                  >
                    <DeleteOutlineIcon />
                  </IconButton>
                </Stack>
                <DurationFields
                  control={control}
                  prefix={`overrides.${index}.rule`}
                  disabled={!write.canWrite}
                />
              </Stack>
            </Paper>
          ))}
        </Stack>
        {submitError ? <ErrorState error={submitError} compact /> : null}
        <Stack direction="row" spacing={1} sx={{ mt: 2 }}>
          <Button
            type="submit"
            variant="contained"
            disabled={!write.canWrite || save.isPending}
            data-testid="time-submit"
          >
            {t('common:actions.save')}
          </Button>
        </Stack>
      </Box>
    </Paper>
  );
}
